import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { AlertTriangle, ChevronRight, X, Search, Banknote, Smartphone, ShoppingCart } from 'lucide-react';
import api from '../../api';

const EXPENSE_TYPES = [
  { type: 'Fijo', icon: '🔁', color: '#FF6B6B' },
  { type: 'Insumos', icon: '🛒', color: '#4ECDC4' },
  { type: 'Extra', icon: '➕', color: '#FFD93D' },
];

// Pie chart of operational expenses only. "Pagos a Proveedores" is shown separately
// (see providerPaymentsPeriod) since it's a debt/cash movement, not an operational expense.
const CHART_TYPES = EXPENSE_TYPES;

const PERIODS = [
  { key: 'daily', label: 'Hoy' },
  { key: 'weekly', label: 'Semana' },
  { key: 'monthly', label: 'Mes' },
  { key: 'yearly', label: 'Año' },
  { key: 'custom', label: 'Rango' },
];

const fmt = (n) => `$${(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const parseUTC = (iso) => {
  if (!iso) return null;
  if (!iso.endsWith('Z') && !/[+-]\d{2}:?\d{2}$/.test(iso)) return new Date(iso.replace(' ', 'T') + 'Z');
  return new Date(iso);
};
const getArgDate = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Argentina/Buenos_Aires' });

const getPeriodRange = (period) => {
  const todayStr = getArgDate();
  const today = new Date(todayStr + 'T12:00:00');
  if (period === 'daily') return { from: todayStr, to: todayStr };
  if (period === 'weekly') {
    const d = new Date(today);
    const dow = d.getDay();
    d.setDate(d.getDate() + (dow === 0 ? -6 : 1 - dow));
    return { from: d.toLocaleDateString('en-CA'), to: todayStr };
  }
  if (period === 'monthly') {
    const y = today.getFullYear();
    const m = String(today.getMonth() + 1).padStart(2, '0');
    return { from: `${y}-${m}-01`, to: todayStr };
  }
  if (period === 'yearly') return { from: `${today.getFullYear()}-01-01`, to: todayStr };
  return null;
};

const CustomTooltip = ({ active, payload }) => {
  if (active && payload?.length) {
    return (
      <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px' }}>
        <p style={{ margin: 0, fontWeight: 700 }}>{payload[0].name}</p>
        <p style={{ margin: 0, color: payload[0].payload.color }}>{fmt(payload[0].value)}</p>
      </div>
    );
  }
  return null;
};

export default function Expenses() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState('monthly');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [form, setForm] = useState({ description: '', amount: '', frequency: 'Mensual', is_recurring: false, payment_method: '' });
  const [pendingTotal, setPendingTotal] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingUrgent, setPendingUrgent] = useState([]);
  const [deleteExpense, setDeleteExpense] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [paymentAlert, setPaymentAlert] = useState(false);
  const [providerDebt, setProviderDebt] = useState(0);
  const [providerPaymentsPeriod, setProviderPaymentsPeriod] = useState(0);

  const getArgToday = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Argentina/Buenos_Aires' });

  const loadPending = useCallback(async () => {
    if (!user?.id) return;
    try {
      const r = await api.get(`/pending-expenses/${user.id}`);
      const items = r.data;
      const tenDaysLater = (() => {
        const today = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Argentina/Buenos_Aires' });
        const d = new Date(today + 'T12:00:00');
        d.setDate(d.getDate() + 10);
        return d.toLocaleDateString('en-CA');
      })();
      const activeItems = items.filter(i => i.due_date <= tenDaysLater);
      setPendingTotal(activeItems.reduce((s, e) => s + e.amount, 0));
      setPendingCount(activeItems.length);
      const today = getArgToday();
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const tomorrowStr = tomorrow.toLocaleDateString('en-CA');
      setPendingUrgent(items.filter(i => i.due_date <= tomorrowStr));
    } catch (e) { console.error(e); }
  }, [user?.id]);

  const load = useCallback(async () => {
    if (!user?.id) return;
    try {
      const range = period === 'custom'
        ? (fromDate && toDate ? { from: fromDate, to: toDate } : null)
        : getPeriodRange(period);
      const params = range ? { start_date: range.from, end_date: range.to } : {};
      const r = await api.get(`/expenses/${user.id}`, { params });
      setExpenses(r.data);
    } catch (e) { console.error(e); }
  }, [user?.id, period, fromDate, toDate]);

  useEffect(() => {
    if (period === 'custom' && (!fromDate || !toDate)) return;
    load();
  }, [load]);

  useEffect(() => { loadPending(); }, [loadPending]);

  useEffect(() => {
    if (!user?.id) return;
    api.get(`/provider-debt/${user.id}`)
      .then(r => setProviderDebt(r.data.total_remaining_debt || 0))
      .catch(() => {});
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    if (period === 'custom' && (!fromDate || !toDate)) return;
    const params = { period };
    if (period === 'custom') { params.from_date = fromDate; params.to_date = toDate; }
    api.get(`/analytics/revenue/${user.id}`, { params })
      .then(r => setProviderPaymentsPeriod(r.data.provider_payments_total || 0))
      .catch(() => {});
  }, [user?.id, period, fromDate, toDate]);

  const openModal = (type) => {
    setSelectedType(type);
    setForm({ description: '', amount: '', frequency: 'Mensual', is_recurring: false, payment_method: '' });
    setPaymentAlert(false);
    setShowModal(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.description || !form.amount) { alert('Completa todos los campos'); return; }
    if (!form.payment_method) {
      setPaymentAlert(true);
      setTimeout(() => setPaymentAlert(false), 3000);
      return;
    }
    setLoading(true);
    try {
      await api.post('/expenses', {
        business_user_id: user?.id,
        expense_type: selectedType,
        description: form.description,
        amount: parseFloat(form.amount),
        frequency: selectedType === 'Fijo' ? form.frequency : null,
        is_recurring: selectedType === 'Fijo' ? form.is_recurring : false,
        payment_method: form.payment_method,
      });
      setShowModal(false);
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
    finally { setLoading(false); }
  };

  const del = (exp) => { setDeleteExpense(exp); };

  const confirmDelExpense = async () => {
    setDeleting(true);
    try {
      await api.delete(`/expenses/${deleteExpense.id}`);
      setDeleteExpense(null);
      load();
    } catch (err) { alert('Error al eliminar'); }
    finally { setDeleting(false); }
  };

  // Search filter
  const filteredExpenses = searchQuery.trim()
    ? expenses.filter(e => e.description.toLowerCase().includes(searchQuery.toLowerCase()))
    : expenses;

  const searchTotal = filteredExpenses.reduce((s, e) => s + e.amount, 0);

  const registeredChartData = CHART_TYPES.map(t => ({
    name: t.type,
    value: filteredExpenses.filter(e => e.expense_type === t.type).reduce((s, e) => s + e.amount, 0),
    color: t.color,
  })).filter(d => d.value > 0);

  const totalExpenses = filteredExpenses.reduce((s, e) => s + e.amount, 0);

  return (
    <div className="page-full">
      <div className="header">
        <button className="icon-btn" onClick={() => navigate(-1)}>←</button>
        <h1 className="header-title">Gastos Varios</h1>
      </div>
      <div className="content-scroll">

        {/* Pending due alert */}
        {pendingUrgent.length > 0 && (
          <div
            data-testid="pending-urgent-alert"
            style={{
              background: 'rgba(255,82,82,0.1)',
              border: '1px solid #ff5252',
              borderRadius: 12,
              padding: '12px 14px',
              marginBottom: 14,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              cursor: 'pointer',
            }}
            onClick={() => navigate('/business/pending-expenses')}
          >
            <AlertTriangle size={20} color="#ff5252" />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, color: '#ff5252', fontSize: '13px' }}>
                {pendingUrgent.length} gasto(s) próximo(s) a vencer
              </div>
              <div style={{ fontSize: '11px', color: 'var(--text2)', marginTop: 1 }}>
                Tocá para ver los detalles
              </div>
            </div>
            <ChevronRight size={16} color="#ff5252" />
          </div>
        )}

        {/* Provider debt alert */}
        {providerDebt > 0 && (
          <div
            data-testid="provider-debt-alert"
            style={{
              background: 'rgba(39,174,96,0.08)',
              border: '1px solid rgba(39,174,96,0.35)',
              borderRadius: 12,
              padding: '12px 14px',
              marginBottom: 10,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              cursor: 'pointer',
            }}
            onClick={() => navigate('/business/purchases')}
          >
            <ShoppingCart size={20} color="#27AE60" />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, color: '#27AE60', fontSize: '13px' }}>
                Deuda pendiente con proveedores
              </div>
              <div style={{ fontSize: '11px', color: 'var(--text2)', marginTop: 1 }}>
                Tocá para ver y registrar pagos
              </div>
            </div>
            <span style={{ fontWeight: 800, color: '#27AE60', fontSize: '15px' }}>{fmt(providerDebt)}</span>
            <ChevronRight size={16} color="#27AE60" />
          </div>
        )}

        {/* Add expense buttons */}
        <div className="expense-types">
          {EXPENSE_TYPES.map(t => (
            <button key={t.type} data-testid={`expense-type-${t.type.toLowerCase()}`} className="expense-type-btn" style={{ borderColor: t.color }}
              onClick={() => openModal(t.type)}>
              <span style={{ fontSize: '28px' }}>{t.icon}</span>
              <span>{t.type}</span>
            </button>
          ))}

          {/* Gastos a Pagar navigation button */}
          <button
            data-testid="gastos-a-pagar-btn"
            className="expense-type-btn"
            style={{ borderColor: '#4A9EFF', background: 'rgba(74,158,255,0.06)' }}
            onClick={() => navigate('/business/pending-expenses')}
          >
            <span style={{ fontSize: '28px' }}>🔔</span>
            <div style={{ flex: 1, textAlign: 'left' }}>
              <div>Gastos a Pagar</div>
              <div style={{ fontSize: '11px', color: 'var(--text2)', fontWeight: 400, marginTop: 2 }}>
                Crea recordatorio de Pagos
              </div>
            </div>
            {pendingCount > 0 && (
              <span style={{ fontSize: '14px', fontWeight: 800, color: '#4A9EFF' }}>
                {fmt(pendingTotal)}
              </span>
            )}
            <ChevronRight size={18} color="#4A9EFF" style={{ flexShrink: 0 }} />
          </button>

          {/* Gastos Personales button */}
          <button
            data-testid="gastos-personales-btn"
            className="expense-type-btn"
            style={{ borderColor: '#9B59B6', background: 'rgba(155,89,182,0.06)' }}
            onClick={() => navigate('/business/personal-expenses')}
          >
            <span style={{ fontSize: '28px' }}>👤</span>
            <div style={{ flex: 1, textAlign: 'left' }}>
              <div>Gastos Personales</div>
              <div style={{ fontSize: '11px', color: 'var(--text2)', fontWeight: 400, marginTop: 2 }}>
                Control de gastos personales
              </div>
            </div>
            <ChevronRight size={18} color="#9B59B6" style={{ flexShrink: 0 }} />
          </button>

          {/* Compras / Proveedores button */}
          <button
            data-testid="compras-proveedores-btn"
            className="expense-type-btn"
            style={{ borderColor: '#27AE60', background: 'rgba(39,174,96,0.06)' }}
            onClick={() => navigate('/business/purchases')}
          >
            <ShoppingCart size={26} color="#27AE60" style={{ flexShrink: 0 }} />
            <div style={{ flex: 1, textAlign: 'left' }}>
              <div>Compras / Proveedores</div>
              <div style={{ fontSize: '11px', color: 'var(--text2)', fontWeight: 400, marginTop: 2 }}>
                {providerPaymentsPeriod > 0 ? `Pagado en este período: ${fmt(providerPaymentsPeriod)}` : 'Registrá compras a proveedores'}
              </div>
            </div>
            {providerDebt > 0 && (
              <span style={{ fontSize: '13px', fontWeight: 800, color: '#FF5252', flexShrink: 0 }}>{fmt(providerDebt)}</span>
            )}
            <ChevronRight size={18} color="#27AE60" style={{ flexShrink: 0 }} />
          </button>
        </div>

        {/* Period Tabs */}
        <div className="period-tabs" style={{ marginBottom: 0 }}>
          {PERIODS.map(p => (
            <button key={p.key} data-testid={`expense-period-${p.key}`} className={`period-tab ${period === p.key ? 'active' : ''}`}
              onClick={() => setPeriod(p.key)}>{p.label}</button>
          ))}
        </div>

        {period === 'custom' && (
          <div className="date-range-row">
            <div className="form-group" style={{ flex: 1 }}>
              <label className="label">Desde</label>
              <input className="input" type="date" data-testid="expense-range-from" value={fromDate} onChange={e => setFromDate(e.target.value)} />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="label">Hasta</label>
              <input className="input" type="date" data-testid="expense-range-to" value={toDate} onChange={e => setToDate(e.target.value)} />
            </div>
          </div>
        )}

        {/* Search Bar */}
        <div style={{ padding: '12px 0 0', position: 'relative' }}>
          <div style={{ display: 'flex', alignItems: 'center', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '8px 14px', gap: 8 }}>
            <Search size={16} color="var(--text2)" />
            <input
              data-testid="expense-search-input"
              style={{ flex: 1, background: 'none', border: 'none', outline: 'none', color: 'var(--text)', fontSize: '14px' }}
              placeholder="Buscar gasto por descripción..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text2)', padding: 0 }}>
                <X size={16} />
              </button>
            )}
          </div>
          {searchQuery.trim() && (
            <div style={{ marginTop: 8, padding: '10px 14px', background: 'rgba(155,89,182,0.08)', border: '1px solid rgba(155,89,182,0.25)', borderRadius: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '13px', color: 'var(--text2)' }}>
                  {filteredExpenses.length} resultado(s) para "{searchQuery}"
                </span>
                <span style={{ fontSize: '15px', fontWeight: 800, color: '#9B59B6' }}>{fmt(searchTotal)}</span>
              </div>
            </div>
          )}
        </div>

        {/* Chart */}
        {registeredChartData.length > 0 && (
          <div className="card" style={{ marginBottom: 16, marginTop: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <span style={{ fontWeight: 700, fontSize: '0.95rem' }}>Distribución de Gastos</span>
              <span style={{ fontWeight: 800, color: '#FF6B6B', fontSize: '1rem' }}>{fmt(totalExpenses)}</span>
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
              {registeredChartData.map(d => (
                <div key={d.name} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.78rem' }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: d.color }} />
                  <span style={{ color: 'var(--text2)' }}>{d.name}</span>
                  <span style={{ fontWeight: 700, color: d.color }}>{fmt(d.value)}</span>
                </div>
              ))}
            </div>
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie data={registeredChartData} cx="50%" cy="50%" innerRadius={45} outerRadius={75} paddingAngle={3} dataKey="value">
                  {registeredChartData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Expense list */}
        <h2 className="section-title" style={{ marginTop: registeredChartData.length > 0 ? 0 : 16 }}>Gastos Registrados</h2>
        {filteredExpenses.map(exp => (
          <div
            key={exp.id}
            className="card-row"
            data-testid={`expense-item-${exp.id}`}
            style={{ cursor: 'pointer' }}
            onClick={() => setSelectedExpense(exp)}
          >
            <div className="card-row-info">
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '6px' }}>
                <span className="expense-type-tag">{exp.expense_type}</span>
                {exp.frequency && <span className="orange-text" style={{ fontSize: '12px' }}>{exp.frequency}</span>}
                {exp.is_recurring && (
                  <span style={{ fontSize: '11px', background: '#FF6B6B22', color: '#FF6B6B', padding: '2px 7px', borderRadius: 4, fontWeight: 600 }}>
                    Recurrente
                  </span>
                )}
                {exp.payment_method && (
                  <span style={{ fontSize: '11px', background: exp.payment_method === 'efectivo' ? 'rgba(78,205,196,0.12)' : 'rgba(74,158,255,0.12)', color: exp.payment_method === 'efectivo' ? '#4ECDC4' : '#4A9EFF', padding: '2px 7px', borderRadius: 4, fontWeight: 600 }}>
                    {exp.payment_method === 'efectivo' ? 'Efectivo' : 'Transferencia'}
                  </span>
                )}
              </div>
              <div className="card-row-title" style={{ fontSize: '15px' }}>{exp.description}</div>
              <div style={{ fontSize: '18px', fontWeight: '700', color: '#ff4444' }}>${(exp.amount || 0).toLocaleString('es-AR')}</div>
              <div style={{ fontSize: '11px', color: 'var(--text2)', marginTop: 4 }}>Ver detalle ›</div>
            </div>
            <button
              className="icon-btn-sm danger"
              data-testid={`delete-expense-${exp.id}`}
              onClick={e => { e.stopPropagation(); del(exp); }}
            >🗑️</button>
          </div>
        ))}
        {filteredExpenses.length === 0 && (
          <div className="empty-state">
            <span className="empty-icon">💰</span>
            <p className="empty-text">{searchQuery ? 'No hay resultados para tu búsqueda' : 'No hay gastos en este período'}</p>
          </div>
        )}
      </div>

      {/* Register Expense Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3 className="modal-title">{selectedType}</h3>
            <form onSubmit={save}>
              <div className="form-group">
                <label className="label">Descripción *</label>
                <input className="input" data-testid="expense-description-input" value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })} required />
              </div>
              <div className="form-group">
                <label className="label">Monto *</label>
                <input className="input" type="number" step="0.01" data-testid="expense-amount-input"
                  value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} required />
              </div>

              {/* Payment Method - Required */}
              <div className="form-group">
                <label className="label">Forma de pago *</label>
                {paymentAlert && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(255,82,82,0.1)', border: '1px solid #ff5252', borderRadius: 8, padding: '8px 12px', marginBottom: 8, fontSize: '12px', color: '#ff5252', fontWeight: 600 }}>
                    <AlertTriangle size={14} /> Seleccioná una forma de pago
                  </div>
                )}
                <div className="payment-row">
                  <button
                    type="button"
                    data-testid="expense-payment-efectivo"
                    className={`payment-btn ${form.payment_method === 'efectivo' ? 'active' : ''}`}
                    onClick={() => setForm({ ...form, payment_method: 'efectivo' })}
                  >
                    <Banknote size={16} /> Efectivo
                  </button>
                  <button
                    type="button"
                    data-testid="expense-payment-transferencia"
                    className={`payment-btn ${form.payment_method === 'transferencia' ? 'active' : ''}`}
                    onClick={() => setForm({ ...form, payment_method: 'transferencia' })}
                  >
                    <Smartphone size={16} /> Transferencia
                  </button>
                </div>
              </div>

              {selectedType === 'Fijo' && (
                <>
                  <div className="form-group">
                    <label className="label">Frecuencia</label>
                    <div className="freq-buttons">
                      {['Diario', 'Semanal', 'Mensual'].map(f => (
                        <button key={f} type="button" className={`freq-btn ${form.frequency === f ? 'active' : ''}`}
                          data-testid={`freq-btn-${f.toLowerCase()}`}
                          onClick={() => setForm({ ...form, frequency: f })}>{f}</button>
                      ))}
                    </div>
                  </div>
                  <div className="form-group" style={{ display: 'flex', alignItems: 'flex-start', gap: 10, background: 'rgba(255,107,107,0.08)', borderRadius: 8, padding: '10px 12px' }}>
                    <input
                      type="checkbox"
                      id="recurring-check"
                      data-testid="expense-recurring-check"
                      checked={form.is_recurring}
                      onChange={e => setForm({ ...form, is_recurring: e.target.checked })}
                      style={{ width: 18, height: 18, marginTop: 2, accentColor: '#FF6B6B', cursor: 'pointer', flexShrink: 0 }}
                    />
                    <label htmlFor="recurring-check" style={{ cursor: 'pointer', fontSize: '0.88rem', lineHeight: 1.4 }}>
                      <strong>Este gasto repite según periodo seleccionado</strong>
                      <br />
                      <span style={{ color: 'var(--text2)', fontSize: '0.78rem' }}>
                        Aparecerá automáticamente en cualquier período que consultes
                      </span>
                    </label>
                  </div>
                </>
              )}
              <div className="modal-buttons">
                <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
                <button type="submit" className="btn-primary" data-testid="save-expense-btn" disabled={loading}>
                  {loading ? 'Guardando...' : 'Registrar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Expense Detail Modal */}
      {selectedExpense && (
        <div className="modal-overlay" onClick={() => setSelectedExpense(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxHeight: '85vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 className="modal-title" style={{ margin: 0 }}>Detalle de Gasto</h3>
              <button onClick={() => setSelectedExpense(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text2)' }}>
                <X size={22} />
              </button>
            </div>

            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <span style={{
                display: 'inline-block',
                padding: '5px 18px',
                borderRadius: 20,
                fontWeight: 700,
                fontSize: '14px',
                background: selectedExpense.expense_type === 'Fijo' ? 'rgba(255,107,107,0.15)' :
                             selectedExpense.expense_type === 'Insumos' ? 'rgba(78,205,196,0.15)' :
                             'rgba(255,217,61,0.15)',
                color: selectedExpense.expense_type === 'Fijo' ? '#FF6B6B' :
                        selectedExpense.expense_type === 'Insumos' ? '#4ECDC4' : '#d4a800',
              }}>
                {selectedExpense.expense_type}
              </span>
            </div>

            <div style={{ textAlign: 'center', marginBottom: 20, padding: '14px', background: 'var(--surface)', borderRadius: 12 }}>
              <div style={{ fontSize: '11px', color: 'var(--text2)', marginBottom: 4 }}>Monto</div>
              <div style={{ fontSize: '34px', fontWeight: 800, color: '#ff5252' }}>{fmt(selectedExpense.amount)}</div>
            </div>

            {[
              { label: 'Descripción', value: selectedExpense.description },
              { label: 'Categoría', value: selectedExpense.expense_type },
              {
                label: 'Fecha de registro',
                value: selectedExpense.created_at
                  ? parseUTC(selectedExpense.created_at)?.toLocaleString('es-AR', {
                      timeZone: 'America/Argentina/Buenos_Aires',
                      day: '2-digit', month: '2-digit', year: 'numeric',
                      hour: '2-digit', minute: '2-digit', hour12: false,
                    })
                  : '-',
              },
              selectedExpense.payment_method
                ? {
                    label: 'Forma de pago',
                    value: selectedExpense.payment_method === 'efectivo' ? 'Efectivo' : 'Transferencia',
                    valueStyle: { color: selectedExpense.payment_method === 'efectivo' ? '#4ECDC4' : '#4A9EFF', fontWeight: 700 },
                  }
                : null,
              selectedExpense.expense_type === 'Fijo' && selectedExpense.frequency
                ? { label: 'Frecuencia', value: selectedExpense.frequency }
                : null,
              {
                label: 'Gasto recurrente',
                value: selectedExpense.is_recurring ? 'Sí' : 'No',
                valueStyle: { color: selectedExpense.is_recurring ? '#4ECDC4' : 'var(--text2)' },
              },
            ].filter(Boolean).map(({ label, value, valueStyle }) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                <span style={{ fontSize: '13px', color: 'var(--text2)', flexShrink: 0, marginRight: 12 }}>{label}</span>
                <span style={{ fontSize: '14px', fontWeight: 600, textAlign: 'right', ...valueStyle }}>{value}</span>
              </div>
            ))}

            <button className="btn-secondary" style={{ width: '100%', marginTop: 20 }} onClick={() => setSelectedExpense(null)}>
              Cerrar
            </button>
          </div>
        </div>
      )}

      {/* Delete expense confirm modal */}
      {deleteExpense && (
        <div className="modal-overlay" onClick={() => setDeleteExpense(null)}>
          <div className="modal-content modal-center" onClick={e => e.stopPropagation()}>
            <h3 className="modal-title">Eliminar Gasto</h3>
            <p style={{ textAlign: 'center', color: 'var(--text2)', marginBottom: 20 }}>
              ¿Eliminar <strong style={{ color: 'var(--text)' }}>{deleteExpense.description}</strong>?
              <br />Esta acción no se puede deshacer.
            </p>
            <div className="modal-buttons">
              <button className="btn-secondary" onClick={() => setDeleteExpense(null)}>Cancelar</button>
              <button
                data-testid="confirm-delete-expense-btn"
                className="btn-danger"
                onClick={confirmDelExpense}
                disabled={deleting}
                style={{ flex: 1 }}
              >
                {deleting ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
